/**
 * Side-by-side row alignment — pure module for unit-testability.
 *
 * Strategy (`comparison-visual-language.md` §3.1):
 *
 *   1. Walk both children arrays in parallel keeping (li, ri) cursors.
 *   2. Use `ChangeResponse.positionA` / `positionB` as anchors and emit
 *      one row per loop iteration via the following case ladder:
 *
 *        Case 1 — pure addition  (positionA == null): consume ri only.
 *        Case 2 — pure deletion  (positionB == null): consume li only.
 *        Case 3 — modification with aligned cursors (li == positionA AND
 *                 ri == positionB on the same change id): consume one from
 *                 each side.
 *        Case 4 — move: drain interim equal rows on the right up to the
 *                 move target, then emit the move row.
 *        Case 5a — left cursor sits on a modification whose right
 *                 counterpart hasn't been reached yet (positionB > ri).
 *                 Emit a right-only equal row to advance ri toward
 *                 positionB without consuming the left modification slot
 *                 prematurely. Symmetric Case 5b handles the mirror case.
 *                 Without this, post–B1 engine output (where positionA
 *                 may differ from positionB by several blocks) collapses
 *                 modifications into equal rows and loses inline diff /
 *                 amber highlighting.
 *        Case 6 — equal: both cursors advance.
 *
 *   The output is a flat list of rows; the React side maps each to a
 *   2-col grid line so that a "modification" pair renders left content +
 *   right content with naturally-aligned tops (CSS grid align-items:
 *   stretch).
 *
 * Edge cases:
 *   - The engine de-dupes pageBreak nodes via `_block_text` returning '',
 *     so we still emit equal rows for them.
 *   - When a change index falls outside the children array (shouldn't
 *     happen but defensive), we emit a placeholder row and continue.
 */
import type { ChangeResponse } from '../../lib/veridoc-api';
import type { DocNode } from '../doc-viewer/DocViewer.types';

export type RowKind =
  | 'equal'
  | 'addition'
  | 'deletion'
  | 'modification'
  | 'move'
  | 'format';

export interface AlignedRow {
  kind: RowKind;
  /** Left-side block (null when this row represents a pure addition). */
  leftBlock: DocNode | null;
  /** Right-side block (null when this row represents a pure deletion). */
  rightBlock: DocNode | null;
  /** Backing change record (null for equal rows). */
  change: ChangeResponse | null;
  /** Stable React key — derived from indices + change id. */
  key: string;
}

interface ChangeIndex {
  byLeft: Map<number, ChangeResponse>;
  byRight: Map<number, ChangeResponse>;
}

function indexChanges(changes: ChangeResponse[]): ChangeIndex {
  const byLeft = new Map<number, ChangeResponse>();
  const byRight = new Map<number, ChangeResponse>();
  for (const change of changes) {
    if (change.positionA != null) byLeft.set(change.positionA, change);
    if (change.positionB != null) byRight.set(change.positionB, change);
  }
  return { byLeft, byRight };
}

export function buildAlignedRows(
  leftChildren: DocNode[],
  rightChildren: DocNode[],
  changes: ChangeResponse[],
): AlignedRow[] {
  const { byLeft, byRight } = indexChanges(changes);
  const rows: AlignedRow[] = [];

  let li = 0;
  let ri = 0;
  let safety = 0;
  const MAX_ITERS = (leftChildren.length + rightChildren.length) * 4 + 16;

  while (li < leftChildren.length || ri < rightChildren.length) {
    if (++safety > MAX_ITERS) {
      // Defensive escape — a malformed changes array could otherwise loop.
      break;
    }
    const leftChange = li < leftChildren.length ? byLeft.get(li) : undefined;
    const rightChange = ri < rightChildren.length ? byRight.get(ri) : undefined;

    // Case 1: pure addition at the right cursor — left side advances zero.
    if (rightChange && rightChange.positionA == null) {
      rows.push({
        kind: 'addition',
        leftBlock: null,
        rightBlock: rightChildren[ri] ?? null,
        change: rightChange,
        key: `add-${rightChange.id}`,
      });
      ri += 1;
      continue;
    }

    // Case 2: pure deletion at the left cursor — right side advances zero.
    if (leftChange && leftChange.positionB == null) {
      rows.push({
        kind: 'deletion',
        leftBlock: leftChildren[li] ?? null,
        rightBlock: null,
        change: leftChange,
        key: `del-${leftChange.id}`,
      });
      li += 1;
      continue;
    }

    // Case 3: modification or format — the same change appears on both sides
    // at their respective cursors. Consume one block from each.
    if (
      leftChange &&
      (leftChange.changeType === 'modification' ||
        leftChange.changeType === 'format') &&
      rightChange &&
      rightChange.id === leftChange.id
    ) {
      rows.push({
        kind: leftChange.changeType,
        leftBlock: leftChildren[li] ?? null,
        rightBlock: rightChildren[ri] ?? null,
        change: leftChange,
        key: `${leftChange.changeType}-${leftChange.id}`,
      });
      li += 1;
      ri += 1;
      continue;
    }

    // Case 4: move — symmetric to modification but with mismatched
    // positions. We render the move at whichever cursor reaches it first
    // and skip the matching index on the other side.
    if (leftChange && leftChange.changeType === 'move') {
      const targetRi = leftChange.positionB ?? ri;
      // Drain any pure equal/insert rows on the right that come before
      // the move target so vertical alignment is preserved.
      while (ri < targetRi && ri < rightChildren.length) {
        const interim = byRight.get(ri);
        if (interim && interim.id !== leftChange.id) {
          // A different change wants this slot — let the next loop iter
          // handle it. Bail out without consuming li to keep state sane.
          break;
        }
        rows.push({
          kind: 'equal',
          leftBlock: null,
          rightBlock: rightChildren[ri] ?? null,
          change: null,
          key: `eq-r-${ri}`,
        });
        ri += 1;
      }
      rows.push({
        kind: 'move',
        leftBlock: leftChildren[li] ?? null,
        rightBlock:
          targetRi < rightChildren.length ? rightChildren[targetRi] : null,
        change: leftChange,
        key: `move-${leftChange.id}`,
      });
      li += 1;
      if (ri === targetRi) ri += 1;
      continue;
    }

    if (rightChange && rightChange.changeType === 'move') {
      // Right cursor reached the move target before the left one did.
      rows.push({
        kind: 'move',
        leftBlock:
          rightChange.positionA != null
            ? leftChildren[rightChange.positionA] ?? null
            : null,
        rightBlock: rightChildren[ri] ?? null,
        change: rightChange,
        key: `move-${rightChange.id}`,
      });
      ri += 1;
      // Skip the corresponding left index if we haven't passed it yet.
      if (rightChange.positionA != null && rightChange.positionA >= li) {
        li = rightChange.positionA + 1;
      }
      continue;
    }

    // Case 5a — leftChange is a modification whose right counterpart
    // hasn't been reached yet (positionB > ri). Emit a right-only equal
    // row so ri can advance toward the modification's positionB without
    // dragging li forward and consuming the left modification block
    // prematurely. Without this branch, post–B1 engine output (where
    // positionA may differ from positionB by several blocks) falls
    // through to Case 6 and the modification degenerates into equal
    // rows on both sides — losing inline diff and amber highlighting.
    if (
      leftChange &&
      (leftChange.changeType === 'modification' ||
        leftChange.changeType === 'format') &&
      leftChange.positionB != null &&
      leftChange.positionB > ri
    ) {
      rows.push({
        kind: 'equal',
        leftBlock: null,
        rightBlock: ri < rightChildren.length ? rightChildren[ri] : null,
        change: null,
        key: `eq-r-${li}-${ri}`,
      });
      ri += 1;
      continue;
    }

    // Case 5b — symmetric: rightChange is a modification whose left
    // counterpart sits ahead (positionA > li). Emit a left-only equal
    // row to advance li without consuming the right modification slot.
    if (
      rightChange &&
      (rightChange.changeType === 'modification' ||
        rightChange.changeType === 'format') &&
      rightChange.positionA != null &&
      rightChange.positionA > li
    ) {
      rows.push({
        kind: 'equal',
        leftBlock: li < leftChildren.length ? leftChildren[li] : null,
        rightBlock: null,
        change: null,
        key: `eq-l-${li}-${ri}`,
      });
      li += 1;
      continue;
    }

    // Case 6: equal — both cursors advance.
    rows.push({
      kind: 'equal',
      leftBlock: li < leftChildren.length ? leftChildren[li] : null,
      rightBlock: ri < rightChildren.length ? rightChildren[ri] : null,
      change: null,
      key: `eq-${li}-${ri}`,
    });
    if (li < leftChildren.length) li += 1;
    if (ri < rightChildren.length) ri += 1;
  }

  return rows;
}

export interface ChangeStats {
  total: number;
  additions: number;
  deletions: number;
  modifications: number;
  moves: number;
  formats: number;
}

export function summariseChanges(changes: ChangeResponse[]): ChangeStats {
  const stats: ChangeStats = {
    total: changes.length,
    additions: 0,
    deletions: 0,
    modifications: 0,
    moves: 0,
    formats: 0,
  };
  for (const change of changes) {
    switch (change.changeType) {
      case 'addition':
        stats.additions += 1;
        break;
      case 'deletion':
        stats.deletions += 1;
        break;
      case 'modification':
        stats.modifications += 1;
        break;
      case 'move':
        stats.moves += 1;
        break;
      case 'format':
        stats.formats += 1;
        break;
    }
  }
  return stats;
}
